import React, { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react'
import AuthController from './controllers/AuthController'
import AppContext from './appContext'
import { bind, onEnter, wrapLoader } from './wrapper'
import { useLocation, useNavigate } from 'react-router-dom'
import AppState from './controllers/AppState'
import { useValidation } from './validation'
import { focusFirstInput } from './components/Fields'
import Dialog from './components/Dialog'
import AuthLayout from './components/AuthLayout'

const ForgotPassword: React.FC<{ show: boolean, setShow: Dispatch<SetStateAction<boolean>> }> = (props) => {
    const [email, setEmail] = useState('')
    const [message, setMessage] = useState('')

    const forgot = () => {
        AuthController.forgotPassword({
            email
        }).then(data => {
            if (data) {
                setMessage('Please check your email.')
            } else {
                setMessage('Could not find email. Please try again.')
            }
        })
    }

    function cancel () {
        setEmail('')
        setMessage('')
        props.setShow(false)
    }

    return (
        <Dialog mounted={focusFirstInput} title="Forgotten your password?" show={props.show} setShow={props.setShow}
            body={
                message === ''
                    ? <div className="p-5">
                        <p className="text-sm text-gray-600">Enter your details below and click send to receive
                            instructions per email to set your password.</p>
                        <label className="mt-4 block text-sm text-gray-700">Email</label>
                        <input type="text"
                            className="mt-1 w-full rounded-lg bg-gray-100 px-4 py-3 outline-none focus:ring-2 focus:ring-primary-400"
                            {...bind(email, setEmail)}/>
                        <div className="mt-5 flex justify-end gap-2">
                            <button className="rounded-lg px-4 py-2 text-sm text-gray-600 hover:bg-gray-100" onClick={cancel}>Cancel</button>
                            <button className="rounded-lg bg-success-600 px-5 py-2 text-sm font-semibold text-white shadow hover:bg-success-700" onClick={forgot}>Submit</button>
                        </div>
                    </div>
                    : <div className="p-5">
                        <p className="text-sm text-gray-700">{message}</p>
                        <div className="mt-5 flex justify-end">
                            <button className="rounded-lg bg-success-600 px-5 py-2 text-sm font-semibold text-white shadow hover:bg-success-700" onClick={cancel}>Ok</button>
                        </div>
                    </div>
            }
        />
    )
}

const Login: React.FC<{ setInitial: (state: AppState) => void }> = (props) => {
    const app = useContext(AppContext)
    const navigate = useNavigate()
    const [forgotShow, setForgotShow] = useState(false)

    useEffect(() => {
        if (app.initial.loggedIn) {
            navigate('/')
        }
    }, [app, navigate])

    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const [error, setError] = useState('')

    const location = useLocation()

    const validation = useValidation({
        usernameRequired: () => username.length > 0,
        passwordRequired: () => password.length > 0
    })

    function login () {
        if (!validation.validate()) { return }

        wrapLoader(app, AuthController.login({
            user: username,
            pass: password,
            rememberMe: false
        }), data => {
            props.setInitial(data)

            if (data.loggedIn) {
                // @ts-ignore
                const from = location.state?.from?.pathname || '/'
                navigate(from)
            } else {
                setError(data.message)
            }
        })
    }

    return (
        <AuthLayout>
            <div className="text-center">
                <img src="/images/at-mark.png" alt="" className="mx-auto h-24 w-auto"/>
                <h2 className="mt-5 font-display text-4xl font-semibold text-secondary-600">Welcome back</h2>
                <p className="mt-1 text-[27px] font-display font-normal text-gray-500">Login to your account</p>
            </div>

            <div className="mt-8 space-y-5" onKeyUp={onEnter(login)}>
                <div>
                    <label className="mb-1 block text-base text-gray-500">Username</label>
                    <input className="w-full rounded-lg bg-gray-100 px-4 py-3 outline-none focus:ring-2 focus:ring-primary-400"
                        type="text"
                        autoFocus {...bind(username, setUsername)}/>
                    {!validation.rules.usernameRequired &&
                        <div className="mt-1 text-sm text-error-500">Username is required</div>}
                </div>

                <div>
                    <label className="mb-1 block text-base text-gray-500">Password</label>
                    <input className="w-full rounded-lg bg-gray-100 px-4 py-3 outline-none focus:ring-2 focus:ring-primary-400"
                        type="password" {...bind(password, setPassword)}/>
                    {!validation.rules.passwordRequired &&
                        <div className="mt-1 text-sm text-error-500">Password is required</div>}
                </div>

                <button className="w-full rounded-lg bg-success-600 py-3 text-2xl tracking-wide text-white shadow-md transition hover:bg-success-700"
                    onClick={login}>LOGIN
                </button>

                {error && <div className="text-center text-sm text-error-500">{error}</div>}

                <button className="text-base text-gray-500 hover:text-primary-700"
                    onClick={_ => setForgotShow(true)}>Forgot Password?</button>
            </div>

            <ForgotPassword setShow={setForgotShow} show={forgotShow}></ForgotPassword>
        </AuthLayout>
    )
}

export default Login
